import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * @typedef {object} ModalDialogProps
 * @property {boolean} isOpen - Show/Don't show the dialog. Allows transitions and persistent mount if needed.
 * @property {string | undefined} [className] - Optional class.  Defaults to 'modal'
 * @property {() => void} onClose - Callback when dialog closes. Component does not manage open/close state.
 * @property {React.ReactNode} children - Dialog contents.
 */

/**
 * Create a handler for clicking outside of the dialog
 * @param {() => void} onClose - Callback to call when the dialog should close.
 */
function createOutsideClickHandler(onClose) {
  /**
   * Handles clicks outside the dialog area.
   * If user clicks outside the bounds of the dialog, onClose is called
   *
   * @param {MouseEvent} event
   */
  return function handleOutsideClick(event) {
    const target = /** @type {HTMLDialogElement} */ (event.currentTarget);
    const rect = target.getBoundingClientRect();

    const isOutside = event.clientX < rect.left
      || event.clientX > rect.right
      || event.clientY < rect.top
      || event.clientY > rect.bottom;

    if (isOutside && event.target === event.currentTarget) {
      onClose();
    }
  };
}

/**
 * A functional modal dialog component.
 *
 * This component provides a minimal modal implementation. It does not manage the open/close state,
 * itself.  Instead, it relies on the parent component to control the `isOpen` prop.
 *
 * @param {ModalDialogProps} props
 */
export function ModalDialog(props) {
  /** @type {React.MutableRefObject<HTMLDialogElement | undefined>} */
  const dialogRef = useRef();
  const [portalElementAvailable, setPortalElementAvailable] = useState(false);
  const {
    className = 'modal', isOpen, children, onClose,
  } = props;

  useEffect(() => {
    const dialog = document.createElement('dialog');
    document.body.appendChild(dialog);

    dialogRef.current = dialog;

    setPortalElementAvailable(true);

    return () => {
      if (dialogRef.current) {
        document.body.removeChild(dialogRef.current);
        dialogRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = createOutsideClickHandler(onClose);
    if (portalElementAvailable) {
      dialogRef.current?.addEventListener('click', handleOutsideClick);
    }
    return () => dialogRef.current?.removeEventListener('click', handleOutsideClick);
  }, [onClose, portalElementAvailable]);

  const dialogClassName = portalElementAvailable ? className : '';

  useEffect(() => dialogRef.current?.setAttribute('class', dialogClassName), [dialogClassName]);

  const shouldShow = isOpen && portalElementAvailable;

  useEffect(() => {
    if (shouldShow) {
      dialogRef.current?.showModal();
    }
  }, [shouldShow]);

  return shouldShow && dialogRef.current
    ? ReactDOM.createPortal(children, dialogRef.current)
    : null;
}
